import { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { relTime, fmtDate } from "@/lib/orgUtils";
import { Search, Building2, Rocket, AlarmClock, BadgeDollarSign, Users, Network, CalendarPlus, Ban, RotateCcw } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";

const PLAN_STYLES = {
  trial: { bg: "#FDAB3D26", text: "#c97a10", label: "Trial" },
  monthly: { bg: "#579BFC26", text: "#2b6cb8", label: "Monthly" },
  annual: { bg: "#00C87526", text: "#00925a", label: "Annual" },
};

const Kpi = ({ label, value, accent, icon: Icon, testid }) => (
  <div data-testid={testid} className="p-4 rounded-xl bg-white border border-[#181B34]/10 shadow-sm min-w-36"
    style={accent ? { borderTop: `4px solid ${accent}` } : {}}>
    <div className="flex items-center justify-between">
      <p className="text-3xl font-black">{value}</p>
      {Icon && <Icon size={17} className="text-[#C4C7D4]" />}
    </div>
    <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#9699AD] mt-1">{label}</p>
  </div>
);

const PlanBadge = ({ plan, status }) => {
  const p = PLAN_STYLES[plan?.billing_cycle] || PLAN_STYLES.trial;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: p.bg, color: p.text }}>{p.label}</span>
      {status === "suspended" && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#E2445C]/15 text-[#E2445C]">Suspended</span>}
    </span>
  );
};

export default function OperatorPage() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => api.get("/operator/orgs").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => (data?.orgs || []).filter((o) =>
    !q || o.name.toLowerCase().includes(q.toLowerCase()) || o.slug.includes(q.toLowerCase()) || o.owner?.email?.includes(q.toLowerCase())
  ), [data, q]);

  const act = async (orgId, fn, msg) => {
    setBusy(orgId);
    try { await fn(); toast.success(msg); await load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };

  if (!data) {
    return <div className="h-full flex items-center justify-center"><div className="w-8 h-8 rounded-full border-4 border-[#6161FF] border-t-transparent animate-spin" /></div>;
  }
  const k = data.kpis;

  return (
    <div className="h-full overflow-auto p-6" data-testid="operator-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-black tracking-tight">Operator Console</h1>
            <p className="text-sm text-[#676a82] font-medium mt-1">Every customer workspace on the Organa platform — plans, trials and usage at a glance.</p>
          </div>
          <div className="relative w-72">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9699AD]" />
            <input data-testid="operator-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search orgs, slugs, owners…"
              className="w-full h-10 pl-9 pr-3 rounded-xl bg-white border border-[#181B34]/10 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#6161FF]/25" />
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
          <Kpi label="Organizations" value={k.total_orgs} icon={Building2} testid="op-kpi-orgs" />
          <Kpi label="Active Trials" value={k.active_trials} accent="#FDAB3D" icon={Rocket} testid="op-kpi-trials" />
          <Kpi label="Expiring ≤ 7d" value={k.expiring_7d} accent="#E2445C" icon={AlarmClock} testid="op-kpi-expiring" />
          <Kpi label="Paid Plans" value={k.paid_orgs} accent="#00C875" icon={BadgeDollarSign} testid="op-kpi-paid" />
          <Kpi label="Suspended" value={k.suspended} icon={Ban} testid="op-kpi-suspended" />
          <Kpi label="Total Users" value={k.total_users} accent="#579BFC" icon={Users} testid="op-kpi-users" />
          <Kpi label="People Mapped" value={k.total_people} accent="#A25DDC" icon={Network} testid="op-kpi-people" />
          <Kpi label="Signups (30d)" value={k.signups_30d} accent="#6161FF" testid="op-kpi-signups" />
        </div>

        <div className="mt-5 grid lg:grid-cols-4 gap-4">
          <div className="lg:col-span-3 bg-white rounded-2xl border border-[#181B34]/10 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] font-bold uppercase tracking-[0.15em] text-[#9699AD] border-b border-[#181B34]/8">
                  <th className="px-4 py-3">Organization</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Owner</th>
                  <th className="px-4 py-3 text-center">Users</th><th className="px-4 py-3 text-center">People</th><th className="px-4 py-3 text-center">Charts</th>
                  <th className="px-4 py-3">Renewal</th><th className="px-4 py-3">Last Activity</th><th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => {
                  const isTrial = o.plan?.billing_cycle === "trial";
                  const suspended = o.status === "suspended";
                  const dl = o.trial_days_left;
                  return (
                    <tr key={o.id} data-testid={`org-row-${o.slug}`} className={`border-b border-[#181B34]/5 ${suspended ? "opacity-60" : ""}`}>
                      <td className="px-4 py-2.5">
                        <p className="font-bold">{o.name}</p>
                        <p className="text-[10px] font-mono text-[#9699AD]">{o.slug} · since {fmtDate(o.created_at)}</p>
                      </td>
                      <td className="px-4 py-2.5"><PlanBadge plan={o.plan} status={o.status} /></td>
                      <td className="px-4 py-2.5">
                        <p className="font-semibold text-[#4b4e63] text-xs">{o.owner?.name || "—"}</p>
                        <p className="text-[10px] text-[#9699AD]">{o.owner?.email}</p>
                      </td>
                      <td className="px-4 py-2.5 text-center font-bold">{o.users}</td>
                      <td className="px-4 py-2.5 text-center font-bold">{o.headcount}</td>
                      <td className="px-4 py-2.5 text-center font-bold">{o.charts}</td>
                      <td className="px-4 py-2.5 text-xs font-semibold">
                        {o.plan?.renewal_date || "—"}
                        {isTrial && dl !== null && (
                          <span className={`ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${dl <= 7 ? "bg-[#E2445C]/15 text-[#E2445C]" : "bg-[#FDAB3D]/15 text-[#c97a10]"}`}>
                            {dl < 0 ? "expired" : `${dl}d left`}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-xs font-semibold text-[#676a82]">{o.last_activity ? relTime(o.last_activity) : "—"}</td>
                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        {isTrial && (
                          <button data-testid={`extend-trial-${o.slug}`} disabled={busy === o.id} title="Extend trial by 30 days"
                            onClick={() => act(o.id, () => api.post(`/operator/orgs/${o.id}/extend-trial`, { days: 30 }), `${o.name}: trial extended by 30 days`)}
                            className="inline-flex items-center gap-1 px-2 h-7 rounded-lg bg-[#FDAB3D]/15 text-[#c97a10] text-[11px] font-bold hover:bg-[#FDAB3D]/30 disabled:opacity-40 mr-1.5">
                            <CalendarPlus size={12} /> +30d
                          </button>
                        )}
                        <select data-testid={`plan-select-${o.slug}`} value={o.plan?.billing_cycle || "trial"} disabled={busy === o.id}
                          onChange={(e) => act(o.id, () => api.put(`/operator/orgs/${o.id}/plan`, { billing_cycle: e.target.value }), `${o.name}: plan set to ${e.target.value}`)}
                          className="text-[11px] font-bold bg-transparent border border-[#181B34]/10 rounded-lg px-1.5 py-1 mr-1.5">
                          <option value="trial">Trial</option><option value="monthly">Monthly</option><option value="annual">Annual</option>
                        </select>
                        <button data-testid={`suspend-toggle-${o.slug}`} disabled={busy === o.id} title={suspended ? "Reactivate workspace" : "Suspend workspace"}
                          onClick={() => (suspended || window.confirm(`Suspend "${o.name}"? Their users will not be able to sign in.`)) &&
                            act(o.id, () => api.put(`/operator/orgs/${o.id}/plan`, { status: suspended ? "active" : "suspended" }), suspended ? `${o.name} reactivated` : `${o.name} suspended`)}
                          className={`inline-flex w-7 h-7 rounded-lg items-center justify-center ${suspended ? "bg-[#00C875]/15 text-[#00925a] hover:bg-[#00C875]/30" : "text-[#676a82] hover:bg-[#E2445C]/10 hover:text-[#E2445C]"} disabled:opacity-40`}>
                          {suspended ? <RotateCcw size={13} /> : <Ban size={13} />}
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-[#676a82] font-medium">No organizations match your search.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="bg-white rounded-2xl border border-[#181B34]/10 p-4" data-testid="signup-trend-card">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#9699AD]">Workspace signups — last 6 months</p>
            <div className="mt-3 h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.signup_trend}>
                  <XAxis dataKey="month" tick={{ fontSize: 10, fontWeight: 700 }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} width={22} />
                  <Tooltip cursor={{ fill: "#F0F3FF" }} contentStyle={{ borderRadius: 12, fontWeight: 700, fontSize: 12 }} />
                  <Bar dataKey="signups" fill="#6161FF" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="mt-2 text-xs text-[#9699AD] font-medium">Self-serve signups create a 30-day trial. Convert them before the clock runs out.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
